import type { CSSProperties } from 'react';
import type { SliceMetric } from '@death-star/shared';
import type { WindowKey } from '../../routes/slicing.js';
import { LoopSelect } from '../LoopSelect.js';
import { isNoBaselineMetric } from './format.js';

const WINDOWS: WindowKey[] = ['7d', '30d', '90d', 'since-2026-01-01'];

const WINDOW_LABELS: Record<WindowKey, string> = {
  '7d': 'Last 7 days',
  '30d': 'Last 30 days',
  '90d': 'Last 90 days',
  'since-2026-01-01': 'Since 2026-01-01',
};

const ALL_REPOS = '__all__';

const legendSwatch = (color: string): CSSProperties => ({
  display: 'inline-block',
  width: 9,
  height: 9,
  background: color,
  verticalAlign: 'middle',
  marginRight: 4,
  borderRadius: 2,
});

export interface FilterBarProps {
  org: string;
  orgs: string[];
  repos: string[];
  selectedRepos: string[];
  window: WindowKey;
  metric: SliceMetric;
  onOrgChange: (org: string) => void;
  onReposChange: (repos: string[] | null) => void;
  onWindowChange: (w: WindowKey) => void;
}

export function FilterBar({ org, orgs, repos, selectedRepos, window: win, metric, onOrgChange, onReposChange, onWindowChange }: FilterBarProps) {
  // The bar chart hides the baseline series for no-baseline metrics
  // (auto_approval, override_rate). Keep the legend in lock-step so we don't
  // advertise a "baseline" swatch the chart never draws.
  const showBaseline = !isNoBaselineMetric(metric);
  const repoValue =
    selectedRepos.length === repos.length
      ? ALL_REPOS
      : selectedRepos[0] ?? ALL_REPOS;

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end', marginBottom: 16 }}>
      <LoopSelect
        id="slicing-org-select"
        label="Org"
        value={org}
        onChange={onOrgChange}
        options={[
          { value: '', label: 'All orgs' },
          ...orgs.map((o) => ({ value: o, label: o })),
        ]}
      />

      {org !== '' && (
        <LoopSelect
          id="slicing-repo-select"
          label="Repo"
          value={repoValue}
          onChange={(v) => {
            if (v === ALL_REPOS) onReposChange(null);
            else onReposChange([v]);
          }}
          options={[
            { value: ALL_REPOS, label: 'All repos in org' },
            ...repos.map((r) => ({ value: r, label: r })),
          ]}
        />
      )}

      <LoopSelect
        id="slicing-window-select"
        label="Window"
        value={win}
        onChange={(v) => onWindowChange(v as WindowKey)}
        options={WINDOWS.map((w) => ({ value: w, label: WINDOW_LABELS[w] }))}
      />

      <div style={{ flex: 1 }} />

      <div style={{ fontSize: '0.75rem', color: 'var(--loop-neutral-700)', paddingBottom: 10, whiteSpace: 'nowrap' }}>
        {showBaseline && (
          <span style={{ marginRight: 8 }}><span style={legendSwatch('var(--loop-neutral-500)')} />baseline</span>
        )}
        <span><span style={legendSwatch('var(--loop-brand-600)')} />current</span>
      </div>
    </div>
  );
}
